/**
 * lib/consent/use-personalization-consent.ts
 *
 * Reactive React binding for {@link hasPersonalizationConsent}. Returns whether
 * the visitor currently allows CMS personalization tracking, and re-renders when
 * consent changes so every tracking site can turn itself on/off live.
 *
 * Deny-by-default: the initial state is `false` (SSR and the first client render
 * agree, avoiding hydration mismatch and never leaking a pre-consent event). On
 * mount it reads the real value, then listens for OneTrust's `OneTrustGroupsUpdated`
 * window event (fired on consent load and on every change, including revocation)
 * to stay in sync.
 *
 * Generic on purpose: any personalization tracking site (page views, `identify`,
 * `<Experience>` impressions/clicks, custom metrics) gates on this one hook, so
 * future metrics/components inherit consent handling for free.
 */

'use client';

import { useEffect, useState } from 'react';

import { hasPersonalizationConsent } from './personalization-consent';

/** OneTrust dispatches this on `window` when consent loads or changes. */
const ONETRUST_UPDATED_EVENT = 'OneTrustGroupsUpdated';

export function usePersonalizationConsent(): boolean {
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    const sync = () => setConsented(hasPersonalizationConsent());

    // Read once now (covers the case where OneTrust already loaded before mount),
    // then follow every consent change.
    sync();
    window.addEventListener(ONETRUST_UPDATED_EVENT, sync);

    return () => window.removeEventListener(ONETRUST_UPDATED_EVENT, sync);
  }, []);

  return consented;
}
